import { useEffect, useState } from 'react';

import { EMPTY_WELLNESS_DOMAINS, WELLNESS_DATA_MULTIPLIER } from '@/constants';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import { useAppStore } from '@/store/useAppStore';
import { getAssessmentsResultLastScore } from '@/utils/queryOptions';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import roles from '@/constants/roles';
import UserPeerSupportBioModal from '@/modules/user/UserPeerSupportBioModal';
import UserSupportTeamSection from '@/modules/user/UserSupportTeamSection';
import { useUserDashboard } from '@/hooks/user/useUserDashboard';
import WholenessRingWithOverview from '@/components/organisms/WholenessRingWithOverview';
import WholenessTrends from '@/components/molecules/WholenessTrends';
import useSyncUserDataToStore from '@/hooks/user/useSyncUserDataToStore';
import UserDashboardSkeleton from '@/components/molecules/Skeletons/UserDashboardSkeleton';
import useDomainScreenerInterpretation from '@/hooks/screener/useDomainScreenerInterpretation';
import GrowthZoneDomainItem from '@/modules/user/growthZone/GrowthZoneDomainItem';
import { EGrowthDomainGoalTrend } from '@/types/GrowthZone.types';
import { THealthDomain, THealthDomainWithOverall } from '@/types/Health.types';
import DomainInterpretationModal from '@/modules/user/DomainInterpretationModal';
import { GOAL_NEXT_STEP_DESCRIPTION } from '@/constants/stringVars';
import DiscussWithMemberCareCard from '@/modules/user/DiscussWithMemberCareCard';
import { primary, text } from '@/theme/colors/palettes';
import RetakeScreenerReminderWithButton from '@/modules/screener/RetakeScreenerReminderWithButton';
import NavigateToGrowthZone from '@/modules/growth-zone/NavigateToGrowthZone';
import SmartFrameworkWithTooltip from '@/modules/growth-zone/SmartFrameworkWithTooltip';

const UserDashboard = () => {
  useSyncUserDataToStore(); // re-fetch user data on dashboard check

  const { user: userData } = useAppStore();

  const [isNewPeerSupportModalOpen, setIsNewPeerSupportModalOpen] = useState(false);

  useEffect(() => {
    if (userData?.roles.includes(roles.peerSupport) && !userData.user_metadata.bio) {
      setIsNewPeerSupportModalOpen(true);
    }
  }, [userData]);

  const { data: userDashboard, isRefetching: isRefetchingUserDashboard } = useUserDashboard();

  const { data: overallInterpretation, refetch, isRefetching } = useDomainScreenerInterpretation('overall');

  const [selectedDomain, setSelectedDomain] = useState<THealthDomainWithOverall>();

  const isLoading =
    !userData || !userDashboard || userDashboard?.score.domains === EMPTY_WELLNESS_DOMAINS || isRefetchingUserDashboard;

  if (isLoading) {
    return <UserDashboardSkeleton />;
  }

  return (
    <VStack width={MAX_CONTAINER_WIDTH}>
      {/* Actual page content */}
      <VStack width={'100%'} marginBottom={'50px'} gap={'80px'}>
        {/* Wholeness Ring and Overview */}
        <VStack marginTop={'20px'} width={'100%'}>
          <VStack width={'100%'} gap={'100px'}>
            <WholenessRingWithOverview
              titleInTopRow
              // dashboardInsights={userDashboard ? userDashboard.insights.perDomain. : undefined}
              dashboardScores={userDashboard ? userDashboard.score : undefined}
              interpretationText={overallInterpretation?.interpretation.description}
              selectedDomain={selectedDomain}
              setSelectedDomain={setSelectedDomain}
              isSendScreenerReminderButtonShown={false}
              additionalOverviewSection={
                <VStack gap={2.5} width={'100%'}>
                  {overallInterpretation?.goals && overallInterpretation.goals.length > 0 ? (
                    <HStack width={'100%'} justify="space-between" alignItems={'center'}>
                      <Text variant={'loraRegular18'} color={primary[500]}>
                        Your Goals
                      </Text>
                      <NavigateToGrowthZone />
                    </HStack>
                  ) : (
                    <VStack gap={1} alignItems={'start'}>
                      <HStack width={'100%'} justify="space-between" alignItems={'center'}>
                        <Text variant={'loraRegular18'} color={primary[500]}>
                          What's Your Next Step?
                        </Text>
                        <NavigateToGrowthZone />
                      </HStack>
                      <Text
                        variant={'urbanistMedium'}
                        color={text.mediumBlue}
                        style={{ fontSize: 16 }}
                        paddingY={'8px'}
                      >
                        {GOAL_NEXT_STEP_DESCRIPTION}
                      </Text>

                      <SmartFrameworkWithTooltip />
                    </VStack>
                  )}

                  <VStack width={'100%'}>
                    <GrowthZoneDomainItem
                      domainGoalData={{
                        createdAt: '',
                        domain: 'overall',
                        goals: overallInterpretation?.goals ?? [],
                        score: 1,
                        trend: EGrowthDomainGoalTrend.IMPROVING,
                      }}
                      headerHidden
                      showMultipleCreateItems
                      refetchAllGoals={refetch}
                      isRefetchingAllGoals={isRefetching}
                    />
                  </VStack>
                </VStack>
              }
              overviewBottomComponent={<RetakeScreenerReminderWithButton containerStyle={{ marginTop: '10px' }} />}
            />

            <HStack width={'100%'} gap={'75px'} alignItems={'start'}>
              <WholenessTrends
                chartHeight={200}
                dashboardTrendData={userDashboard.insights ? userDashboard.trends : null}
              />
              {/* used to pass this to the component below supportTeam={userDashboard.supportTeam} */}
              <DiscussWithMemberCareCard />
            </HStack>
          </VStack>
          {/* )} */}
        </VStack>

        {/* TODO: removed for now */}
        {/* User Notes */}
        {/* <HStack width={'100%'} justifyContent={'center'} gap={'40px'} alignItems={'center'}>
          <UserNotes memberId={userData.user_id} />
        </HStack> */}

        {/* Care Specialists (ex Bobbi section) */}
        <VStack marginTop={'50px'}>
          <Text variant={'loraMediumTitle'} color={'text.darkBlue'}>
            Reach Out for Support
          </Text>

          <UserSupportTeamSection supportTeamCounts={userDashboard.supportTeam ?? { peerSupports: 0, therapists: 0 }} />
        </VStack>

        {/* Call emergency */}
        <Box
          backgroundColor={'background.cloudGray'}
          marginY={'40px'}
          paddingY={'40px'}
          paddingX={'80px'}
          borderRadius={'12px'}
          fontSize={'16px'}
          lineHeight={'20px'}
          fontWeight={500}
          maxWidth={'600px'}
        >
          <Text display={'inline'} variant={'lora'} fontStyle={'italic'}>
            If you feel like you are having a medical emergency{' '}
          </Text>
          <Text display={'inline'} fontWeight={800} color={'primary.500'}>
            dial 911
          </Text>
          <VStack display={'inline-block'} marginTop={'20px'} textAlign={'center'}>
            <Text display={'inline'} variant={'lora'} fontSize={'16px'} lineHeight={'20px'} fontStyle={'italic'}>
              If you are considering suicide, or struggling with your mental health and are having a crisis{' '}
            </Text>
            <Text display={'inline'} fontWeight={800} color={'primary.500'}>
              dial 988
            </Text>
          </VStack>
        </Box>
      </VStack>

      <UserPeerSupportBioModal
        isModalOpen={isNewPeerSupportModalOpen}
        userMetadata={userData.user_metadata}
        setIsModalOpen={setIsNewPeerSupportModalOpen}
      />

      <DomainInterpretationModal
        selectedDomain={selectedDomain}
        setSelectedDomain={setSelectedDomain}
        selectedDomainScore={
          selectedDomain === 'overall'
            ? userDashboard.score.totalScore * WELLNESS_DATA_MULTIPLIER
            : (userDashboard.score.domains[selectedDomain as THealthDomain] ?? 0)
        }
      />

      {/* TODO: removing for now */}
      {/* <OrganizationFeed /> */}
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_user/user/dashboard')({
  staticData: { title: 'Dashboard' },
  component: UserDashboard,
  loader: async ({ context }) => context.queryClient.ensureQueryData(getAssessmentsResultLastScore()),
});
